<template>
  <el-container>
    <el-main>
      <h3 class="heading">New feed</h3>
      <el-card>
        <FeedForm
          v-if="feedItem"
          v-model="feedItem"
          :loading="loading"
          action-text="Create"
          @submit="onSubmit"
          @update:modelValue="update"
        />
      </el-card>
      <LoadingBlock :loading="fetchingFeed" />
    </el-main>
  </el-container>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from 'vue'
import { ElMessage } from 'element-plus'
import {
  AdType,
  FeedValidationErrorsKey,
  Stream,
  QualityType,
  RonType
} from '@/api/types'
import { IFeedAttributes, IFeed } from '@/interfaces/feeds'
import useRouter from '@/hooks/useRouter'
import { useFeedsStore } from '@/store/modules/feeds'
import FeedForm from '../components/FeedForm.vue'
import useValidation from '@/hooks/useValidation'
import useFetchFeed from '@/hooks/useFetchFeed'
import LoadingBlock from '@/components/LoadingBlock.vue'
import { safeSubmitProcessing } from '@/utils/helpers'
import { useSpotsListsStore } from '@/apps/traforama/store/spotsLists'

export default defineComponent({
  name: 'NewFeed',
  components: {
    FeedForm,
    LoadingBlock
  },
  props: {
    id: {
      type: String,
      default: ''
    },
    integrationId: {
      type: String,
      default: ''
    }
  },
  setup(props) {
    const feedsStore = useFeedsStore()
    const spotsListsStore = useSpotsListsStore()

    const defaultFeedItem: IFeedAttributes = {
      id: undefined,
      name: '',
      feed_state: 'new',
      feed_format_id: props.integrationId
        ? Number(props.integrationId)
        : undefined,
      stream: Stream.Adult,
      open_rtb: false,
      url: '',
      type_ids: AdType.Popunder,
      qualities: [QualityType.All],
      country_codes: [],
      device_ids: [],
      browser_ids: [],
      connection_type_ids: [],
      no_bounces: false,
      skip_if_no_ref: false,
      skip_if_no_cookies: false,
      skip_if_x_requested_with: false,
      no_onloop: false,
      block_proxy_type_ids: [],
      use_whitelist_domains: false,
      whitelist_domains: [],
      blacklist_domains: [],
      use_whitelist_subids: false,
      whitelist_subids: [],
      blacklist_subids: [],
      use_whitelist_user_agents: false,
      whitelist_user_agents: [],
      blacklist_user_agents: [],
      cpm_floor_to_confirm: 0,
      platform_ids: [],
      refresh_rate: 0,
      limits: [],
      with_scans: false,
      size_ids: [],
      spot_ids: [],
      spots_list_ids: [],
      ron_type: RonType.All
    }

    const { feed, fetchingFeed, FetchItem } = useFetchFeed()

    // If cloning (props.id exists), fetch the feed, otherwise use default
    if (props.id) {
      FetchItem(props.id, true)
    }

    const feedItem = computed(() => {
      return props.id ? feed.value : defaultFeedItem
    })
    const { provideValidation, setValidationErrors } = useValidation(
      FeedValidationErrorsKey
    )
    const errors = provideValidation()

    const loading = ref(false)

    const { router } = useRouter()

    const onSuccess = async (item?: IFeed) => {
      let route = '/feeds'
      if (
        item &&
        item.attributes &&
        !item.attributes.open_rtb &&
        !item.attributes.feed_format_id
      ) {
        route = `/feeds/${item.id}/integrations/new`
      }

      await router.push(route)
      ElMessage.success({ message: 'Feed has been created' })
    }

    const onSubmit = async function ({
      feed,
      withReview
    }: {
      feed: IFeedAttributes
      withReview: boolean
    }) {
      // @ts-ignore: Object is possibly 'undefined'
      feed.feed_state = withReview ? '10' : '1'
      errors.value = {}
      await safeSubmitProcessing(
        loading,
        () => feedsStore.SaveItem(feed),
        (item?: IFeed) => onSuccess(item),
        (err) => {
          setValidationErrors(err, errors)
        }
      )
    }

    const update = (feed: IFeedAttributes) => {
      spotsListsStore.FetchItems({
        filter: {
          type_id: feed.type_ids,
          ron_type: feed.ron_type
        }
      })
    }

    return { feedItem, errors, loading, onSubmit, fetchingFeed, update }
  }
})
</script>
